import { moneySaid } from "@rally/agreed";
import { Button, Col, Icon, Row, ScrollArea, Tappable, Text, useTheme } from "@rally/ui-kit";
import { useEffect, useState } from "react";
import { say } from "../../localization/say.ts";
import { act, read } from "../../net/rally.ts";
import type { Person } from "../composer/saying.ts";
import { Face, LiveDot, nameFor, saidOf } from "./busy-parts.tsx";
import { asATree, type Busy, quartersOf, runsOf, shownOf } from "./doing.ts";
import { elapsedSaid } from "./working.ts";

type Spend = { today: number; all: number; tokens: number } | null;

const A_ROW_IS = 36;

const A_PANEL_ROW_HOLDS = 44;

function Dial({ row, name, now, onPress }: { row: Busy; name: string; now: number; onPress: () => void }) {
  const theme = useTheme();
  const [near, setNear] = useState(false);
  const quarters = quartersOf(row.startedAt, now);

  if (!row.run || row.why) return <Col style={{ width: 20, height: 20 }} />;
  return (
    <Tappable
      accessibilityRole="button"
      accessibilityLabel={say("busy.stopOne")(name)}
      testID={`busy-stop-${row.account}`}
      onPress={onPress}
      onHoverIn={() => setNear(true)}
      onHoverOut={() => setNear(false)}
      onFocus={() => setNear(true)}
      onBlur={() => setNear(false)}
      style={{ width: 20, height: 20, alignItems: "center", justifyContent: "center" }}
    >
      {near ? (
        <Icon name="stop" size={14} color="danger" />
      ) : (
        <Col
          testID={`busy-dial-${row.account}`}
          accessibilityLabel={`${quarters}`}
          style={{
            width: 14,
            height: 14,
            borderRadius: 999,
            borderWidth: 1,
            borderColor: theme.color["text.tertiary"],
            overflow: "hidden",
            justifyContent: "flex-end",
          }}
        >
          <Col
            style={{
              height: `${quarters * 25}%`,
              backgroundColor: quarters === 4 ? theme.color.danger : theme.color["text.tertiary"],
            }}
          />
        </Col>
      )}
    </Tappable>
  );
}

const A_MANAGERS_FACE = 24;

const A_WORKERS_FACE = 18;

function BusyRow({
  row,
  known,
  now,
  onStop,
  under,
}: {
  row: Busy;
  known: Map<string, Person>;
  now: number;
  onStop: (row: Busy) => void;
  under?: boolean | undefined;
}) {
  const theme = useTheme();
  const clock = elapsedSaid(row.startedAt, now);
  return (
    <Col
      testID={`busy-panel-row-${row.account}`}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: under ? theme.space[2] : theme.space[3],
        paddingVertical: under ? theme.space[1] : theme.space[2],
        paddingHorizontal: theme.space[2],
      }}
    >
      {under ? (
        <Col
          style={{
            width: 1,
            alignSelf: "stretch",
            marginRight: theme.space[1],
            backgroundColor: theme.color["border.default"],
          }}
        />
      ) : null}
      <Face row={row} known={known} size={under ? A_WORKERS_FACE : A_MANAGERS_FACE} />
      <Col style={{ flex: 1, minWidth: 0 }}>
        <Text typeName="caption" color={under ? "text.secondary" : "text.primary"} numberOfLines={1}>
          {nameFor(row, known)}
        </Text>
        <Row gap={1}>
          <LiveDot on={row.run !== null && !row.why} />
          <Text typeName="caption" color="text.tertiary" numberOfLines={1}>
            {saidOf(row, A_PANEL_ROW_HOLDS)}
          </Text>
        </Row>
      </Col>
      {clock ? (
        <Text typeName="caption" color="text.tertiary">
          {clock}
        </Text>
      ) : null}
      <Dial row={row} name={nameFor(row, known)} now={now} onPress={() => onStop(row)} />
    </Col>
  );
}

export function BusyPanel({
  conversation,
  rows,
  known,
  now,
  onStopped,
}: {
  conversation: string;
  rows: readonly Busy[];
  known: Map<string, Person>;
  now: number;
  onStopped: (account: string, why: string) => void;
}) {
  const theme = useTheme();
  const [spend, setSpend] = useState<Spend>(null);
  const { shown, hidden, total } = shownOf(rows);

  const running = runsOf(rows).length;
  useEffect(() => {
    let gone = false;
    const asked = running;
    read<Spend>("conversation.spend", { conversation })
      .then((answer) => {
        if (!gone && asked === running) setSpend(answer);
      })
      .catch(() => {
        if (!gone) setSpend(null);
      });
    return () => {
      gone = true;
    };
  }, [conversation, running]);

  const stop = (row: Busy) => {
    if (!row.run) return;
    onStopped(row.account, say("busy.stopping"));
    void act("run.halt", { run: row.run }).catch(() => undefined);
  };

  const runs = runsOf(rows);
  return (
    <Col
      testID="busy-panel"
      style={{
        borderWidth: 1,
        borderColor: theme.color["border.default"],
        borderRadius: theme.radius.lg,
        backgroundColor: theme.color["bg.surface"],
        overflow: "hidden",
      }}
    >
      <ScrollArea style={{ maxHeight: A_ROW_IS * 10 }}>
        {asATree(shown).map((row) => (
          <Col
            key={row.account}
            style={row.indented ? { paddingLeft: theme.space[4] } : undefined}
            testID={row.indented ? `busy-under-${row.under}` : `busy-row-${row.account}`}
          >
            <BusyRow row={row} known={known} now={now} onStop={stop} under={row.indented} />
          </Col>
        ))}
      </ScrollArea>
      <Col
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: theme.space[2],
          paddingVertical: theme.space[2],
          paddingHorizontal: theme.space[3],
          borderTopWidth: 1,
          borderTopColor: theme.color["border.default"],
        }}
      >
        <Text typeName="caption" color="text.tertiary" testID="busy-panel-total">
          {say("busy.all")(total)}
        </Text>
        {hidden > 0 ? (
          <Text typeName="caption" color="text.tertiary" testID="busy-panel-more">
            {say("busy.andMore")(hidden)}
          </Text>
        ) : null}
        <Col style={{ flex: 1 }} />
        {spend ? (
          <Text typeName="caption" color="text.tertiary" testID="busy-panel-spend">
            {say("working.spentToday")(moneySaid(spend.today))}
          </Text>
        ) : null}
        {runs.length > 0 ? (
          <Button
            testID="busy-stop-all"
            variant="ghost"
            size="sm"
            icon="stop"
            onPress={() => {
              for (const row of rows) stop(row);
            }}
          >
            {say("busy.stopAll")}
          </Button>
        ) : null}
      </Col>
    </Col>
  );
}
